import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  Paginate,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import StoreItemsBulkActions from './bulk-store-edit-actions';
import { AddIcon, FilterAltIcon } from 'cokitchen-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import './styles.scss';
import { debounce, formatCurrency, formatNumberWithCommas } from 'utils/functions';
import { useNavigate } from 'react-router-dom';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import RemoveModal from 'components/modals/remove-modal';
import useInventoryStoreContext from 'contexts/inventory-store.context/context';
import { StoreItemType } from 'models/store-item.model';
import AddStoreItemModal from 'components/modals/add-store-item-modal';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import useDashboardContext from 'contexts/dashboard.context';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';
const tableHeaders = ['Item name', 'Min Qty', 'Price(per unit)', 'Unit ', ''];

const InventoryStoreItems = () => {
  const navigate = useNavigate();
  const { storeItemsToEdit, setStoreItemsToEdit } = useInventoryStoreContext();
  const [storeArray, setStoreArray] = useState<StoreItemType[]>([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showAddStoreItemModal, setShowAddStoreItemModal] = useState(false);
  const [isFetchingStoreItems, setIsFetchingStoreItems] = useState(true);
  const [isArchivingStoreItems, setIsArchivingStoreItems] = useState(false);
  const [totalStoreItems, setTotalStoreItems] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [storeItemsToArchive, setStoreItemsToArchive] = useState<StoreItemType[]>([]);
  const { can } = useRoleContext();
  const canWriteInventoryManagement = can(
    InventoryModule.InventoryManagement,
    RolePermission.Write
  );

  const handleArchive = (items: StoreItemType[]) => {
    if (!canWriteInventoryManagement) return;
    if (!items.length) return;
    setStoreItemsToArchive(items);
    setShowRemoveModal(true);
  };
  const { formIngredientCategoryList } = useDashboardContext();
  const CATEGORY_LIST: string[] = formIngredientCategoryList;

  const isAllSelected = () => {
    return storeArray.length > 0 && storeArray.length === storeItemsToEdit.length;
  };

  const selectStoreItem = (id: string) => {
    const storeItem = storeArray.find((storeItem) => storeItem.id === id);
    if (storeItem) {
      if (storeItemsToEdit.includes(storeItem)) {
        setStoreItemsToEdit(storeItemsToEdit.filter((storeItem) => storeItem.id !== id));
      } else {
        setStoreItemsToEdit([...storeItemsToEdit, storeItem]);
      }
    }
  };

  const toggleAll = () => {
    if (!canWriteInventoryManagement) return;
    if (isAllSelected()) {
      setStoreItemsToEdit([]);
    } else {
      setStoreItemsToEdit(storeArray);
    }
  };
  useEffect(() => {
    setStoreItemsToEdit([]);
  }, []);

  const fetchStoreItems = async (
    searchString: string,
    categoryString: string,
    current_page?: number
  ) => {
    try {
      setIsFetchingStoreItems(true);
      const response = await apiService.get(`ingredients`, {
        params: {
          name: searchString,
          type: 'ingredient',
          category: categoryString === 'All' ? undefined : categoryString,
          page: current_page || '1'
        },
        version: 'v2'
      });
      if (response) {
        setStoreArray(response?.data?.data);
        setLastPage(
          Math.ceil(response?.data?.pagination?.total / response?.data?.pagination?.page_size)
        );
        setTotalStoreItems(response?.data?.pagination?.total);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching store items'}
          type='error'
        />
      );
    } finally {
      setIsFetchingStoreItems(false);
    }
  };

  const debounceFetchStoreItems = useCallback(debounce(fetchStoreItems, 1000), []);

  useEffect(() => {
    setIsFetchingStoreItems(true);
    debounceFetchStoreItems(search, categoryFilter, currentPage);
  }, [search, categoryFilter, currentPage]);

  const confirmArchiveStoreItems = async () => {
    if (!storeItemsToArchive.length) {
      setShowRemoveModal(false);
      return;
    }
    try {
      setIsArchivingStoreItems(true);
      await apiService.put('ingredients/archive', {
        data: { ingredient_ids: storeItemsToArchive.map((storeItem) => storeItem.id) },
        version: 'v2'
      });
      toast(<ToastMessage message='Store item(s) archived successfully' type='success' />);
      setStoreItemsToEdit([]);
      setStoreItemsToArchive([]);
      setShowRemoveModal(false);
      setStoreArray([]);
      await fetchStoreItems(search, categoryFilter, currentPage);
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error archiving store items'}
          type='error'
        />
      );
    } finally {
      setIsArchivingStoreItems(false);
    }
  };

  const updateStoreItemGramPrices = (storeItem: StoreItemType) => {
    const updatedVendorIngredients = storeItem.vendor_ingredients.map((vendorIngredient) => {
      const gramPrice = parseFloat(vendorIngredient.gram_price);
      const updatedGramPrice = gramPrice.toFixed(2);
      return {
        ...vendorIngredient,
        gram_price: updatedGramPrice
      };
    });

    return {
      ...storeItem,
      vendor_ingredients: updatedVendorIngredients
    };
  };

  return (
    <div className='px-2 md:px-6 mt-2 vendors'>
      <div className='flex justify-between items-center mb-8'>
        <div>
          <h4 className='text-[#2E3031] text-2xl font-semibold'>
            Ingredients ({totalStoreItems ? totalStoreItems : isFetchingStoreItems ? '**' : ''})
          </h4>
          <p className='text-[#6F7376] text-sm'>
            Manage and track the quantity of ingredients in stock
          </p>
        </div>
        {/* <Link to={`${MAIN_STORE_ROUTE_KEYS.INVENTORY_STORE_ITEMS}/add`}> */}
        {canWriteInventoryManagement && (
          <Button
            variant='block'
            color='primary'
            className='w-[167px] h-[40px] text-white'
            small
            onClick={() => setShowAddStoreItemModal(true)}>
            <AddIcon className='w-[18px] h-[18px] text-white mr-1' />+ Add Ingredient
          </Button>
        )}
        {/* </Link> */}
      </div>
      <div className='flex justify-between items-center mb-8'>
        <div className='flex gap-3 items-center'>
          <div className='h-[36px] mt-2'>
            <Dropdown
              hideArrow
              placement='bottom-start'
              content={
                <div className='border border-[#E5E6E6] rounded-[5px] text-xs leading-[19px] h-[36px] flex px-2 justify-between items-center w-[114px]  cursor-pointer'>
                  <span className='text-[#5E6164] text-sm truncate'>{categoryFilter}</span>
                  <FilterAltIcon className='text-[#5E6164]' />
                </div>
              }>
              <DropdownList>
                {['All', ...CATEGORY_LIST].map((item, i) => (
                  <DropdownItem
                    className={`capitalize ${categoryFilter === item ? 'text-red-5' : ''}`}
                    key={i}
                    onClick={() => {
                      setStoreArray([]);
                      setCategoryFilter(item);
                      setCurrentPage(1);
                    }}>
                    {item}
                  </DropdownItem>
                ))}
              </DropdownList>
            </Dropdown>
          </div>
          <Input
            type='search'
            placeholder='Search store items'
            className='w-[300px]'
            inputClass='h-[36px]'
            value={search}
            onChange={(e) => {
              setStoreArray([]);
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => {
            setStoreArray([]);
            setCurrentPage(e.selected + 1);
          }}
        />
      </div>
      <div className='max-h-screen overflow-y-auto'>
        <Table
          headers={tableHeaders}
          canSelect={canWriteInventoryManagement}
          isAllSelected={isAllSelected()}
          onToggleAll={toggleAll}
          loading={isFetchingStoreItems}>
          {storeArray.map((storeItem) => (
            <TableRow
              key={storeItem.id}
              canSelect={canWriteInventoryManagement}
              isSelected={storeItemsToEdit.includes(storeItem)}
              className='hover:bg-[#F5F6F6] text-[#5E6164] table_row font-[400]'
              onToggle={() => {
                if (!canWriteInventoryManagement) return;
                selectStoreItem(storeItem.id);
              }}>
              <TableData>
                <p className='font-[500] text-black'>{storeItem.name}</p>
              </TableData>
              <TableData>{formatCurrency(storeItem.min_quantity, { style: 'decimal' })}</TableData>
              <TableData>{formatCurrency(storeItem.min_vendor_price)}</TableData>
              <TableData className='text-left'>
                {formatNumberWithCommas(storeItem.unit_weight) + ' ' + storeItem.unit_type}
              </TableData>
              <TableData>
                {canWriteInventoryManagement ? (
                  <div className='flex gap-3 w-[52px]'>
                    <EditIcon
                      className='w-[18px] h-[18px] icon'
                      onClick={() => {
                        setStoreItemsToEdit([updateStoreItemGramPrices(storeItem)]);
                        navigate(`${MAIN_STORE_ROUTE_KEYS.INVENTORY_STORE_ITEMS_BULK_EDIT}`);
                      }}
                    />
                    <ArchiveIcon
                      className='w-[18px] h-[18px] icon'
                      onClick={() => handleArchive([storeItem])}
                    />
                  </div>
                ) : (
                  ''
                )}
              </TableData>
            </TableRow>
          ))}
        </Table>
      </div>
      {!isFetchingStoreItems && storeArray.length === 0 && (
        <div className='flex justify-center mt-3 h-[50vh] items-center'>
          <p className='text-[#6F7376]'>No store items found</p>
        </div>
      )}
      {showRemoveModal && (
        <RemoveModal
          show
          itemCount={storeItemsToArchive.length}
          loading={isArchivingStoreItems}
          onClose={() => setShowRemoveModal(false)}
          onConfirm={confirmArchiveStoreItems}
        />
      )}
      {canWriteInventoryManagement && storeItemsToEdit.length > 0 && (
        <StoreItemsBulkActions
          item={storeItemsToEdit}
          deleteFunc={handleArchive}
          setSelectedStoreItems={setStoreItemsToEdit}
        />
      )}
      {showAddStoreItemModal && canWriteInventoryManagement && (
        <AddStoreItemModal
          show={showAddStoreItemModal}
          onClose={() => setShowAddStoreItemModal(false)}
        />
      )}
    </div>
  );
};

export default InventoryStoreItems;
